import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

export default function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <Helmet>
        {/* Primary Meta Tags */}
        <title>Privacy Policy | Trademark Service Company</title>
        <meta name="title" content="Privacy Policy | Trademark Service Company" />
        <meta name="description" content="Read our privacy policy to understand how Trademark Service Company collects, uses, and protects your personal information and trademark application data." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={currentUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:title" content="Privacy Policy | Trademark Service Company" />
        <meta property="og:description" content="Read our privacy policy to understand how we collect, use, and protect your personal information and trademark application data." />

        {/* Twitter */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:url" content={currentUrl} />
        <meta name="twitter:title" content="Privacy Policy | Trademark Service Company" />
        <meta name="twitter:description" content="Read our privacy policy to understand how we collect, use, and protect your personal information and trademark application data." />
      </Helmet>

      {/* Header */}
      <header className="bg-gradient-to-r from-[#1e3a8a] to-[#1e40af] text-white py-6 px-4 sm:px-6 lg:px-8 shadow-lg">
        <div className="max-w-5xl mx-auto">
          <a href="/" className="text-lg font-semibold hover:text-[#f59e0b] transition-colors inline-block mb-2">
            ← Back to Home
          </a>
          <h1 className="text-3xl md:text-4xl font-bold">Privacy Policy</h1>
          <p className="text-white/90 mt-2">Trademark Service Company</p>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Card className="border-none shadow-xl">
          <CardContent className="p-8 md:p-12 space-y-8">
            <section>
              <h2 className="text-2xl font-bold text-[#1e3a8a] mb-4">Introduction</h2>
              <p className="text-gray-700 leading-relaxed">
                Trademark Service Company ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and use our trademark registration services. Please read this privacy policy carefully. If you do not agree with the terms of this privacy policy, please do not access the site.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#1e3a8a] mb-4">Information We Collect</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We may collect information about you in a variety of ways. The information we may collect on the website includes:
              </p>
              <div className="space-y-4">
                <div>
                  <h3 className="text-xl font-semibold text-[#1e3a8a] mb-2">Personal Data</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Personally identifiable information, such as your name, shipping address, email address, and telephone number, and demographic information, such as your age, gender, hometown, and interests, that you voluntarily give to us when you register with the website or when you choose to participate in various activities related to the website, such as requesting trademark services, contacting us for support, or submitting inquiries through our contact forms.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-[#1e3a8a] mb-2">Trademark Information</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Information related to your trademark applications, including but not limited to trademark names, logos, descriptions of goods and services, business information, and any other data necessary to prepare and file your trademark application with the United States Patent and Trademark Office (USPTO).
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-[#1e3a8a] mb-2">Financial Information</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Financial information, such as data related to your payment method (e.g., valid credit card number, card brand, expiration date) that we may collect when you purchase, order, return, exchange, or request information about our services. We store only very limited, if any, financial information that we collect. Otherwise, all financial information is stored by our payment processor, and you are encouraged to review their privacy policy and contact them directly for responses to your questions.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-[#1e3a8a] mb-2">Usage Data</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Information our servers automatically collect when you access the website, such as your IP address, your browser type, your operating system, your access times, and the pages you have viewed directly before and after accessing the website. This information helps us understand how visitors use our website and allows us to improve our services.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#1e3a8a] mb-4">Use of Your Information</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Having accurate information about you permits us to provide you with a smooth, efficient, and customized experience. Specifically, we may use information collected about you via the website to:
              </p>
              <ul className="space-y-2 text-gray-700 ml-6">
                <li className="flex items-start gap-2">
                  <span className="text-[#f59e0b] font-bold">•</span>
                  <span>Prepare, file, and manage your trademark applications with the USPTO</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#f59e0b] font-bold">•</span>
                  <span>Create and manage your account and provide customer support</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#f59e0b] font-bold">•</span>
                  <span>Process your transactions and send you related information, including purchase confirmations and invoices</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#f59e0b] font-bold">•</span>
                  <span>Send you administrative information, such as updates to our terms, conditions, and policies</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#f59e0b] font-bold">•</span>
                  <span>Communicate with you about your trademark application status, office actions, and other USPTO-related matters</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#f59e0b] font-bold">•</span>
                  <span>Send you marketing and promotional communications (you can opt-out at any time)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#f59e0b] font-bold">•</span>
                  <span>Improve our website and services based on your feedback and usage patterns</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#f59e0b] font-bold">•</span>
                  <span>Monitor and analyze usage and trends to enhance user experience</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#f59e0b] font-bold">•</span>
                  <span>Prevent fraudulent transactions and protect against criminal activity</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#f59e0b] font-bold">•</span>
                  <span>Comply with legal obligations and respond to legal requests</span>
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#1e3a8a] mb-4">Disclosure of Your Information</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We may share information we have collected about you in certain situations. Your information may be disclosed as follows:
              </p>
              <div className="space-y-4">
                <div>
                  <h3 className="text-xl font-semibold text-[#1e3a8a] mb-2">By Law or to Protect Rights</h3>
                  <p className="text-gray-700 leading-relaxed">
                    If we believe the release of information about you is necessary to respond to legal process, to investigate or remedy potential violations of our policies, or to protect the rights, property, and safety of others, we may share your information as permitted or required by any applicable law, rule, or regulation.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-[#1e3a8a] mb-2">USPTO and Government Agencies</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Your trademark application information will be filed with the United States Patent and Trademark Office (USPTO) and will become part of the public record. We may also share information with other government agencies as required by law or in connection with your trademark registration.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-[#1e3a8a] mb-2">Third-Party Service Providers</h3>
                  <p className="text-gray-700 leading-relaxed">
                    We may share your information with third parties that perform services for us or on our behalf, including payment processing, data analysis, email delivery, hosting services, customer service, and marketing assistance. These service providers are contractually obligated to protect your information and use it only for the purposes for which we disclose it to them.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-[#1e3a8a] mb-2">Business Transfers</h3>
                  <p className="text-gray-700 leading-relaxed">
                    We may share or transfer your information in connection with, or during negotiations of, any merger, sale of company assets, financing, or acquisition of all or a portion of our business to another company.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#1e3a8a] mb-4">Security of Your Information</h2>
              <p className="text-gray-700 leading-relaxed">
                We use administrative, technical, and physical security measures to help protect your personal information. While we have taken reasonable steps to secure the personal information you provide to us, please be aware that despite our efforts, no security measures are perfect or impenetrable, and no method of data transmission can be guaranteed against any interception or other type of misuse. Any information disclosed online is vulnerable to interception and misuse by unauthorized parties. Therefore, we cannot guarantee complete security if you provide personal information.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#1e3a8a] mb-4">Retention of Your Information</h2>
              <p className="text-gray-700 leading-relaxed">
                We will retain your personal information only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. We maintain records of trademark applications and related communications for at least seven years after the completion of services, or as required by professional standards and legal obligations. When we no longer need your information, we will securely delete or anonymize it.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#1e3a8a] mb-4">Your Privacy Rights</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Depending on your location, you may have certain rights regarding your personal information:
              </p>
              <ul className="space-y-2 text-gray-700 ml-6">
                <li className="flex items-start gap-2">
                  <span className="text-[#f59e0b] font-bold">•</span>
                  <span><strong>Access:</strong> You can request access to the personal information we hold about you</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#f59e0b] font-bold">•</span>
                  <span><strong>Correction:</strong> You can request that we correct any inaccurate personal information</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#f59e0b] font-bold">•</span>
                  <span><strong>Deletion:</strong> You can request that we delete your personal information, subject to certain legal exceptions</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#f59e0b] font-bold">•</span>
                  <span><strong>Opt-Out:</strong> You can opt-out of receiving marketing communications from us at any time</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#f59e0b] font-bold">•</span>
                  <span><strong>Data Portability:</strong> You can request a copy of your personal information in a structured, commonly used format</span>
                </li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                To exercise any of these rights, please contact us using the information provided below.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#1e3a8a] mb-4">Cookies and Tracking Technologies</h2>
              <p className="text-gray-700 leading-relaxed">
                We may use cookies, web beacons, tracking pixels, and other tracking technologies on the website to help customize the website and improve your experience. When you access the website, your personal information is not collected through the use of tracking technology. Most browsers are set to accept cookies by default. You can remove or reject cookies, but be aware that such action could affect the availability and functionality of the website. For more information on how we use cookies, please refer to our Cookie Policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#1e3a8a] mb-4">Third-Party Websites</h2>
              <p className="text-gray-700 leading-relaxed">
                The website may contain links to third-party websites and applications of interest, including advertisements and external services, that are not affiliated with us. Once you have used these links to leave the website, any information you provide to these third parties is not covered by this Privacy Policy, and we cannot guarantee the safety and privacy of your information. Before visiting and providing any information to any third-party websites, you should inform yourself of the privacy policies and practices (if any) of the third party responsible for that website, and should take those steps necessary to, in your discretion, protect the privacy of your information.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#1e3a8a] mb-4">Children's Privacy</h2>
              <p className="text-gray-700 leading-relaxed">
                We do not knowingly solicit information from or market to children under the age of 13. If we learn that we have collected personal information from a child under age 13 without verification of parental consent, we will delete that information as quickly as possible. If you believe we might have any information from or about a child under 13, please contact us immediately.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#1e3a8a] mb-4">Changes to This Privacy Policy</h2>
              <p className="text-gray-700 leading-relaxed">
                We reserve the right to make changes to this Privacy Policy at any time and for any reason. We will alert you about any changes by updating the "Last Updated" date of this Privacy Policy. Any changes or modifications will be effective immediately upon posting the updated Privacy Policy on the website, and you waive the right to receive specific notice of each such change or modification. You are encouraged to periodically review this Privacy Policy to stay informed of updates. You will be deemed to have been made aware of, will be subject to, and will be deemed to have accepted the changes in any revised Privacy Policy by your continued use of the website after the date such revised Privacy Policy is posted.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#1e3a8a] mb-4">Contact Us</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                If you have questions or comments about this Privacy Policy, please contact us at:
              </p>
              <div className="bg-gradient-to-br from-[#1e3a8a]/5 to-blue-50 p-6 rounded-lg">
                <p className="text-gray-800 font-semibold mb-2">Trademark Service Company</p>
                <p className="text-gray-700">1420 Kettner Blvd suite 100, San Diego, CA 92101</p>
                <p className="text-gray-700">Email: info@trademarkservicecompany.com</p>
                <p className="text-gray-700">Phone: +1 (123) 123-4567</p>
              </div>
            </section>
          </CardContent>
        </Card>
      </div>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-[#1e3a8a] to-[#1e40af] text-white py-8 px-4 sm:px-6 lg:px-8 mt-12">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-sm text-white/80">© 2026 Trademark Service Company. All rights reserved.</p>
          <div className="mt-4 space-x-6">
            <a href="/" className="text-white/80 hover:text-white text-sm transition-colors">
              Home
            </a>
            <a href="/privacy" className="text-white/80 hover:text-white text-sm transition-colors">
              Privacy Policy
            </a>
            <a href="/terms" className="text-white/80 hover:text-white text-sm transition-colors">
              Terms & Conditions
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
